import { useState, useEffect, useMemo } from 'react';
import { Loader2, FileText, FilePlus } from 'lucide-react';

/**
 * Quotes tab of the Customer Management side panel — the Recent Quotes list (same cards, same tabs, same search, same
 * CRM / edit / delete / stage options) filtered to this one customer. It is also the Quotes panel that slides out on the
 * right of the Create Quote window.
 *
 *   onAddQuote   (optional) shows the orange Add Quote button at the top — the same merged Create Quote window the Add Quote
 *                button in the Customer Management header opens. Leave it out to hide the button.
 *   onOpenQuote  called with the quote when a card's Edit is clicked; the parent decides how the editor opens, so the panel
 *                never opens a quote window of its own.
 *   refreshKey   change this to reload the list (the parent bumps it every time a quote window closes).
 *
 * Quotes.jsx is loaded on demand (not imported at the top) so this panel can never create an import cycle with the
 * modals that Quotes.jsx itself uses.
 */
export default function CustomerQuotesPanel({ account, user, onAddQuote = null, onOpenQuote = null, refreshKey = 0 }) {
  const [QuotesMod, setQuotesMod] = useState(null);

  useEffect(() => {
    let alive = true;
    import('../pages/Quotes').then((m) => { if (alive) setQuotesMod(() => m.default); });
    return () => { alive = false; };
  }, []);

  const customerFilter = useMemo(
    () => ({ zohoAccountId: account?.id || '', name: account?.name || '' }),
    [account?.id, account?.name],
  );

  return (
    <div className="h-full flex flex-col" data-testid="customer-quotes-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
        >
          <FileText className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight">Quotes</h3>
          <p className="text-[11px] text-gray-500 truncate">{account?.name} — quotes (past 12 months)</p>
        </div>
        {onAddQuote && (
          <button
            type="button"
            onClick={onAddQuote}
            className="inline-flex items-center gap-1.5 rounded-xl h-9 px-3 border-2 border-orange-400 text-orange-700 bg-white text-xs font-bold whitespace-nowrap transition-all shadow-sm hover:shadow-md hover:bg-orange-50 active:scale-95 flex-shrink-0"
            title="Create a new quote for this customer"
            data-testid="customer-quotes-add-quote-btn"
          >
            <FilePlus className="w-3.5 h-3.5" /> Add Quote
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {!QuotesMod ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading quotes…</span>
          </div>
        ) : (
          <QuotesMod.RecentQuotesView
            key={`${account?.id}-${account?.name}-${refreshKey}`}
            user={user}
            customerFilter={customerFilter}
            onSelect={(quote) => onOpenQuote?.(quote)}
          />
        )}
      </div>
    </div>
  );
}
